import { isNil } from 'lodash-es';
import { getStringByteLength } from './validation/common';

export const getLocaleKey = (text: string): string =>
  text ? text.replace(/ /g, '_').replace(/_&_/g, '_').toUpperCase() : '';

export const getDetailKey = (text: string): string =>
  text ? text.replace(/ /g, '').replace(/_/g, '').toLowerCase() : '';

export const compressText = ({
  text,
  length = 100,
  suffix = '...',
}: {
  text: string;
  length?: number;
  suffix?: string;
}) => {
  if (text.length <= length) {
    return text;
  }
  return `${text.slice(0, length)}${suffix}`;
};

export const getPascalCase = (text: string, joinValue: string = ''): string => {
  return text
    .split(' ')
    .map((word) => {
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(joinValue);
};

export const getJsonParse = (text: string): Record<any, any> | Array<any> | null => {
  try {
    const parsedData = JSON.parse(text);
    return parsedData;
  } catch {
    return null;
  }
};

export const getJsonFormatted = (
  obj: object | null,
  { space }: { space?: number } = {},
): string => {
  try {
    return JSON.stringify(obj, null, space);
  } catch (e) {
    console.log(e);
    return '';
  }
};

export const stringToBinary = (str?: string) => {
  if (!str) return '';

  const encoder = new TextEncoder();
  return Array.from(encoder.encode(str))
    .map((byte) => byte.toString(2).padStart(8, '0'))
    .join('');
};

export const adjustStringByMaxByte = (text: string, maxByte: number): string => {
  if (!text || !maxByte || getStringByteLength(text) <= maxByte) {
    return text;
  }

  let startIndex = 0;
  let endIndex = text.length;
  let result = text;

  while (startIndex <= endIndex) {
    const midIndex = Math.floor((startIndex + endIndex) / 2);
    const slice = text.slice(0, midIndex);
    const byteLength = getStringByteLength(slice);

    if (byteLength <= maxByte) {
      result = slice;
      startIndex = midIndex + 1;
    } else {
      endIndex = midIndex - 1;
    }
  }
  return result;
};

export const getOnlyNumberString = (
  val: string,
  options: { min?: number; max?: number; decimal?: number; useNegative?: boolean } = {},
) => {
  if (typeof val !== 'string' || !val.trim()) {
    return '';
  }

  const { min, max, decimal, useNegative } = options;

  const allowCharType = ['0-9'];
  if (decimal) {
    allowCharType.push('.');
  }
  if (useNegative) {
    allowCharType.push('-');
  }

  const regExp = new RegExp(`[^${allowCharType.join('')}]`, 'g');
  let num = val.replaceAll(regExp, '');

  if (decimal) {
    const [integerNum, decimalNum] = num.split('.');
    num = decimalNum !== undefined ? `${integerNum}.${decimalNum.slice(0, decimal)}` : integerNum;
  }

  if (Number.isNaN(+num)) {
    return '';
  }
  if (!isNil(min) && +num < min) {
    return `${min}`;
  }
  if (!isNil(max) && +num > max) {
    return `${max}`;
  }
  return num;
};

export const hexToString = (hexStr: string) => {
  let result = '';
  for (let i = 0; i < hexStr.length; i += 2) {
    result += String.fromCharCode(parseInt(hexStr.slice(i, i + 2), 16));
  }
  return result;
};

export const getCommaSeparatedText = (
  values: (string | number)[],
  seprate: string = ',',
): string => {
  if (!values || values.length === 0) {
    return '';
  }

  const stringValues = values.map(String);

  if (stringValues.length === 1) {
    return stringValues[0];
  }

  return stringValues.join(seprate);
};

export interface HighlightPart {
  text: string;
  match: boolean;
}

/**
 * 텍스트를 검색어 매칭/비매칭 구간으로 분할한다 (검색 결과 하이라이트용).
 *
 * - 검색어가 비어 있으면 전체 텍스트를 단일 비매칭 파트로 반환한다.
 * - 모든 매칭 구간을 각각 `match: true` 파트로 분리한다 (연속 매칭도 개별 파트).
 * - 정규식이 아닌 `indexOf` 리터럴 매칭이라 정규식 특수문자(`* ? " / \`)에 안전하다.
 * - 대소문자를 무시해 매칭하되, 반환되는 파트 `text`는 원본 케이스를 유지한다.
 *
 * @param text 원본 텍스트. `undefined`면 빈 문자열로 처리한다.
 * @param search 검색어. 내부에서 trim·lowercase 처리하며 `undefined`를 허용한다.
 * @returns 분할된 파트 배열. 항상 1개 이상을 반환한다.
 * @example
 * splitHighlightParts('Hello World', 'o w');
 * // [{ text: 'Hell', match: false }, { text: 'o W', match: true }, { text: 'orld', match: false }]
 */
export const splitHighlightParts = (
  text: string | undefined,
  search: string | undefined,
): HighlightPart[] => {
  const value = text ?? '';
  const term = (search ?? '').trim().toLowerCase();
  if (!term) {
    return [{ text: value, match: false }];
  }

  const lower = value.toLowerCase();
  const parts: HighlightPart[] = [];
  let idx = 0;
  let found = lower.indexOf(term, idx);
  while (found !== -1) {
    if (found > idx) {
      parts.push({ text: value.slice(idx, found), match: false });
    }
    parts.push({ text: value.slice(found, found + term.length), match: true });
    idx = found + term.length;
    found = lower.indexOf(term, idx);
  }
  if (idx < value.length) {
    parts.push({ text: value.slice(idx), match: false });
  }
  return parts;
};
